import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useApi from "../../hooks/useApi";
import { useModalStore, generateUniqueId } from "../../store/GlobalVarriables";
import { isValidEmail, isOnlyNumbers } from "../../utils/helpers";

import Loader from "../../Components/Loader";
import GlobalActionButtons from "../../Components/GlobalActionButtons/GlobalActionButtons";
import Input from "../../Components/Input";
import ModalConfirmation from "../../Components/ModalConfirmation/ModalConfirmation";
import ModalWarning from "../../Components/ModalWarning/ModalWarning";
import DotsMenu from "../../Components/DotsMenu";
import Weekdays from "../../Components/Weekdays/Weekdays";
import PlayerDetails from "../../Components/PlayerDetails/PlayerDetails";
import InviteCustomers from "../../Components/InviteCustomers";

import { ReactComponent as PayerBadge } from "../../static/images/payer_badge.svg";
import { ReactComponent as DuePayment } from "../../static/images/due_payment.svg";
import { ReactComponent as Tick } from "../../static/images/tick_sign.svg";

import "./style.scss";

const ScheduleDetails = () => {
  const { get, post, del, put, patch, loading } = useApi();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const invRef = useRef(null);
  const subRef = useRef(null);

  const { scheduleID } = useParams();

  const [scheduleDetail, setScheduleDetail] = useState([]);
  const [invitationData, setInvitationData] = useState([]);
  const [subDetails, setSubDetails] = useState([]);

  const [invitationInputToggle, setInvitationInputToggle] = useModalStore("");

  const [confirmModalToggle, setConfirmModalToggle] = useState("");
  const [modalText, setModalText] = useState("");
  const [modalSubText, setModalSubText] = useState("");
  const [modalValue, setModalValue] = useState(0); // for detect what action is needed

  const [warningModalToggle, setWarningModalToggle] = useState("");
  const [warningModalText, setWarningModalText] = useState("");
  const [warningButtonText, setWarningButtonText] = useState("");

  const [price, setPrice] = useState("");
  const [detailMenuToggle, setDetailMenuToggle] = useState(false);

  const [playerDetails, setPlayerDetails] = useState([]);
  const [playerDetailsToggle, setPlayerDetailsToggle] = useState(false);

  const [actionItemId, setActionItemId] = useState("");
  const [groupPayerId, setGroupPayerId] = useState("");
  const [currentPayer, setCurrentPayer] = useState([]);
  const [payerIdentifier, setPayerIdentifier] = useState([]);
  const [invitedCustomers, setInvitedCustomers] = useState([]);
  const [inviteInputValue, setInviteInputValue] = useState("");
  const [inviteHasError, setIviteHasError] = useState(false);

  const menuItems = [
    {
      text: t("invite_customer"),
      disabled: scheduleDetail.availableSeats <= 0,
      onClick: () => {
        handleValidations();
      },
    },
    {
      text: t("cancel_schedule"),
      onClick: () => handleMenuButton("cancel schedule"),
    },
    {
      text: t("edit_schedule"),
      onClick: () => {
        navigate(`/schedule/edit/${scheduleID}`);
      },
    },
    {
      text: t("finance"),
      disabled: true,
    },
    {
      text: t("training_history"),
      onClick: () => {
        navigate("/history/byschedule");
      },
    },
  ];

  const fetchDetails = async () => {
    try {
      const data = await get(`/Schedule/${scheduleID}`);
      setPrice(data.price);
      setScheduleDetail(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchInvitation = async () => {
    try {
      const data = await get(`/Schedule/${scheduleID}/Invitations`);
      if (data) {
        const initializedInvitationData = data.map((item) => ({
          phoneNumber: item.invitedPhoneNumber,
          email: item.invitedEmailAddress,
          consumerName: item.consumerName,
          status: item.status,
          id: item.id,
          showMenu: false,
        }));

        setInvitationData(initializedInvitationData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSub = async () => {
    try {
      const data = await get(`/Schedule/${scheduleID}/Subscriptions`);

      if (data) {
        const initializedSubscriptionData = data.map((item) => ({
          businessAccountName: item.businessAccount.name,
          phoneNumber: item.businessAccount.contactMSISDN,
          email: item.businessAccount.contactEmail,
          overduePayment: item.overduePayment,
          id: item.id,
          isGroupPayer: item.isGroupPayer,
          showMenu: false,
        }));
        setSubDetails(initializedSubscriptionData);
        setCurrentPayer(data.filter((item) => item.isGroupPayer === true));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleGroupPayer = async () => {
    if (currentPayer.length) {
      try {
        const data = await patch(`/Subscription`, {
          id: currentPayer[0].id,
          isGroupPayer: false,
        });
      } catch (error) {}
    }

    try {
      const data = await patch(`/Subscription`, {
        id: groupPayerId,
        isGroupPayer: true,
        // description: "string",
      });

      await fetchSub();
    } catch (error) {
      console.error(error);
    }
  };

  const postInvitation = async () => {
    if (modalSubText === "") {
      setInvitationInputToggle("");
      setInvitedCustomers([]);
      setIviteHasError(false);
      setInviteInputValue("");
    } else if (modalValue === 1) {
      try {
        const data = await del(`/Schedule/${scheduleID}/Cancel`);
        setModalValue(0);
        setWarningModalToggle("modal-warning");
        setWarningModalText(t("cancel_success"));
        setWarningButtonText(t("go_schedule_details"));
        navigate("/sessions");
      } catch (error) {
        console.error(error);
      }
    } else if (modalValue === 2) {
      try {
        const data = await put(`/Invitation`, {
          id: actionItemId,
          operation: "Cancel",
        });
        setModalValue(0);
        setWarningModalToggle("modal-warning");
        setWarningModalText(t("cancel_success"));
        setWarningButtonText(t("go_schedule_details"));
      } catch (error) {
        console.error(error);
      }
    } else if (modalValue === 3) {
      try {
        const data = await del(`/Subscription/${actionItemId}/Cancel`);
        setModalValue(0);
        setWarningModalToggle("modal-warning");
        setWarningModalText(t("cancel_success"));
        setWarningButtonText(t("go_schedule_details"));
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        const invitations = invitedCustomers.map((customer) => ({
          scheduleId: scheduleID,
          price,
          ...(isValidEmail(customer.contact) && {
            customerEmailAddress: customer.contact,
          }),
          ...(isOnlyNumbers(customer.contact) && {
            customerPhoneNumber: customer.contact,
          }),
        }));
        const data = await post(`/Invitation`, {
          invitations,
        });
        setModalValue(0);
        setWarningModalToggle("modal-warning");
        setWarningModalText(t("invitation_sent_success"));
        setWarningButtonText(t("go_schedule_details"));
      } catch (error) {
        console.error("Error fetching invite:", error);
      }
    }
  };

  const handleValidations = () => {
    /// disappear invitation menu
    setInvitationData(
      invitationData.map((item, i) => ({
        ...item,
        showMenu: false,
      })),
    );

    /// disappear subscription menu
    setSubDetails(
      subDetails.map((item, i) => ({
        ...item,
        showMenu: false,
      })),
    );

    if (scheduleDetail.availableSeats > 0) {
      setInvitationInputToggle("invite");
      setDetailMenuToggle(false);
    }

    if (invitationInputToggle === "invite") {
      if (
        inviteInputValue.length > 5 &&
        (isValidEmail(inviteInputValue) || isOnlyNumbers(inviteInputValue))
      ) {
        setIviteHasError(true);
        return;
      }

      setConfirmModalToggle("modal-confirm");
      setModalText(t("create_invitation_confirm_text"));
      setModalSubText(t("create_invitation_confirmation_text"));
    }
  };

  const handleCancel = (buttonText) => {
    if (buttonText === "back") {
      navigate("/schedules");
    } else {
      if (invitedCustomers.length === 0) {
        setInvitationInputToggle("");
      } else {
        setConfirmModalToggle("modal-confirm");
        setModalText(t("page_leave_warning"));
        setModalSubText("");
      }
    }
  };

  const handleMenuButton = async (identity, payerItem) => {
    setDetailMenuToggle(false);

    if (identity === "cancel schedule") {
      setConfirmModalToggle("modal-confirm");
      setModalText(t("cancel_schedule_confirmation_modal_text"));
      setModalSubText(t("cancel_schedule_confirmation_modal_subText"));
      setModalValue(1);
    } else if (identity === "cancel invitation") {
      setConfirmModalToggle("modal-confirm");
      setModalText(t("cancel_invitation_confirmation_modal_text"));
      setModalSubText(t("cancel_invitation_confirmation_modal_subText"));
      setModalValue(2);
      /// disappear invitation menu
      setInvitationData(
        invitationData.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    } else if (identity === "cancel subscription") {
      setConfirmModalToggle("modal-confirm");
      setModalText(t("cancel_subscription_confirmation_modal_text"));
      setModalSubText(t("cancel_subscription_confirmation_modal_subText"));
      setModalValue(3);
      /// disappear subscription menu
      setSubDetails(
        subDetails.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    } else if (identity === "select as group payer") {
      if (payerIdentifier.isGroupPayer === true) {
        return;
      }

      setSubDetails(
        subDetails.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );

      setConfirmModalToggle("modal-confirm");
      setModalText(
        t("add_into_group_confirmation_modal_text", {
          name: payerItem.businessAccountName,
          scheduleName: scheduleDetail.description.trim(""),
        }),
      );
      setModalSubText(t("add_into_group_confirmation_modal_subText"));
      setModalValue(4);
      setGroupPayerId(payerItem.id);
    } else if (identity === "player details") {
      setPlayerDetails(payerItem);
      setPlayerDetailsToggle(true);

      /// disappear subscription menu

      setSubDetails(
        subDetails.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );

      /// disappear invitation menu
      setInvitationData(
        invitationData.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    }
  };

  const toggleMenu = (identity, index) => {
    if (identity === "invitation") {
      setInvitationData(
        invitationData.map((item, i) =>
          i === index
            ? { ...item, showMenu: !item.showMenu }
            : { ...item, showMenu: false },
        ),
      );

      /// disappear subscription menu
      setSubDetails(
        subDetails.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    } else {
      setSubDetails(
        subDetails.map((item, i) =>
          i === index
            ? { ...item, showMenu: !item.showMenu }
            : { ...item, showMenu: false },
        ),
      );
      /// disappear invitation menu
      setInvitationData(
        invitationData.map((item, i) => ({
          ...item,
          showMenu: false,
        })),
      );
    }
  };

  const handleToggleDetailsMenu = () => {
    setDetailMenuToggle((prevState) => !prevState);
  };

  const handleClickOutside = (event) => {
    if (invRef.current && !invRef.current.contains(event.target)) {
      setInvitationData(
        invitationData.map((item) => ({
          ...item,
          showMenu: false,
        })),
      );
    }
    if (subRef.current && !subRef.current.contains(event.target)) {
      setSubDetails(
        subDetails.map((item) => ({
          ...item,
          showMenu: false,
        })),
      );
    }
  };

  const handleInviteCutomer = (customer) => {
    if (customer.trim() === "") return;
    setInvitedCustomers([
      ...invitedCustomers,
      { contact: customer.trim(), id: generateUniqueId() },
    ]);
    setIviteHasError(false);
  };

  const handleDeleteCustomer = (id) => {
    setInvitedCustomers(
      invitedCustomers.filter((customer) => customer.id !== id),
    );
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [invitationData]);

  const warningButton = () => {
    setInvitationInputToggle("");
    fetchDetails();
    fetchSub();
    fetchInvitation();
  };

  const convertDate = (value) => {
    const date = new Date(value);

    const day = date.getDate();
    const month = date.getMonth() + 1;

    const monthName = t(`months.${month}`);

    const formattedDate = `${day} ${monthName}`;

    return formattedDate;
  };

  useEffect(() => {
    try {
      fetchDetails();
      fetchInvitation();
      fetchSub();
    } catch (err) {
      console.error("Error:", err);
    }
  }, []);

  return (
    <div className={`detail-page`}>
      <div className="details-container">
        <div className="schedule-week-days">
          {scheduleDetail.isOneTime ? (
            <h2>{convertDate(scheduleDetail.effectiveFrom)}</h2>
          ) : (
            <>
              {scheduleDetail?.scheduleDays && (
                <Weekdays weekdaysData={scheduleDetail.scheduleDays} />
              )}
            </>
          )}
          <div className="details-description">
            <span>{scheduleDetail.description}</span>
            <div className="deatils-menu">
              <DotsMenu
                menuItems={menuItems}
                isMenuOpen={detailMenuToggle}
                toggleMenu={() => handleToggleDetailsMenu()}
              />
            </div>
          </div>
        </div>

        <div className="financial-details">
          <h4>{t("financial_section_header")}</h4>

          <ul>
            {scheduleDetail.isOneTime ? (
              <li>{t("details_onetime_event")}</li>
            ) : (
              <>
                {scheduleDetail.groupSchedule ? (
                  <li>
                    {scheduleDetail?.financialDetails?.payerOption ===
                    "Individual"
                      ? t("details_paymentType_individual")
                      : t("details_paymentType_onePay")}
                  </li>
                ) : (
                  <li>{t("details_individual_schedule")}</li>
                )}
              </>
            )}

            <li>
              {t("details_price", {
                price: scheduleDetail?.price,
                period:
                  scheduleDetail?.financialDetails?.pricingType === "Monthly"
                    ? `/ ${t("month")}`
                    : `/ ${t("day")}`,
              })}
            </li>
            <li>
              {t(
                scheduleDetail?.financialDetails?.billingPeriod ===
                  "End_Of_The_Month"
                  ? "details_billingType_end_of_month"
                  : scheduleDetail?.financialDetails?.billingPeriod ===
                      "Beginning_of_the_Month"
                    ? "details_billingType_beginning_of_month"
                    : scheduleDetail?.financialDetails?.paymentType ===
                        "PrePaid"
                      ? "details_billingType_based_on_events_prePaid"
                      : "details_billingType_based_on_events_postPaid",
                {
                  count: scheduleDetail?.financialDetails?.billingEventCount,
                },
              )}
            </li>
            <li>
              {scheduleDetail?.financialDetails?.paymentType === "PrePaid"
                ? t(
                    scheduleDetail.groupSchedule
                      ? "details_paymentType_prePaid_group"
                      : "details_paymentType_prePaid_individual",
                    {
                      count:
                        scheduleDetail?.financialDetails?.prepaymentEventCount,
                    },
                  )
                : t(
                    scheduleDetail.groupSchedule
                      ? "details_paymentType_postPaid_group"
                      : "details_paymentType_postPaid_individual",
                  )}
            </li>
          </ul>
        </div>
        {invitationData.length > 0 ? (
          <div className={`sub-details-container ${invitationInputToggle}`}>
            {invitationData.map((item, index) => {
              return (
                <div
                  className="single-sub"
                  key={item.id}
                  onClick={() => {
                    setPlayerDetailsToggle(true);
                    setPlayerDetails(item);
                  }}
                >
                  <div className="single-sub-name">
                    {item.consumerName ? (
                      <h3>{item.consumerName}</h3>
                    ) : item.phoneNumber ? (
                      <h3>{item.phoneNumber}</h3>
                    ) : (
                      <h3>{item.email}</h3>
                    )}
                  </div>
                  <div
                    className="single-sub-icons"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <div className="single-sub-status">
                      <span>{item.status}</span>
                    </div>
                    <div className="details-settings">
                      <DotsMenu
                        menuItems={[
                          {
                            text: t("cancel_invitation"),
                            onClick: () =>
                              handleMenuButton("cancel invitation"),
                          },
                          {
                            text: t("player_details"),
                            onClick: () =>
                              handleMenuButton("player details", item),
                          },
                        ]}
                        isMenuOpen={item.showMenu}
                        toggleMenu={() => {
                          toggleMenu("invitation", index);
                          setActionItemId(item.id);
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          subDetails.length === 0 && (
            <div className={`empty-data ${invitationInputToggle}`}>
              {t("details_dont_have_subscription")}
            </div>
          )
        )}

        {subDetails.length > 0 ? (
          <div className={`sub-details-container ${invitationInputToggle}`}>
            {subDetails.map((item, index) => {
              return (
                <div
                  className="single-sub"
                  key={item.id}
                  onClick={() => {
                    setPayerIdentifier(item);
                    setPlayerDetailsToggle(true);
                    setPlayerDetails(item);
                  }}
                >
                  <div className="single-sub-name">
                    <h3>{item.businessAccountName}</h3>
                  </div>
                  <div
                    className="single-sub-icons"
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {scheduleDetail?.financialDetails?.payerOption ===
                    "Group" ? (
                      <>
                        {item.isGroupPayer === true ? (
                          <div className="group-payer">
                            <PayerBadge />
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {item.overduePayment === true ? (
                      <div className="group-payer">
                        <DuePayment />
                      </div>
                    ) : null}

                    {scheduleDetail?.financialDetails?.payerOption ===
                    "Group" ? (
                      <div className="details-settings">
                        <DotsMenu
                          menuItems={[
                            {
                              text: t("cancel_subscription"),
                              onClick: () =>
                                handleMenuButton("cancel subscription"),
                            },

                            {
                              text: t("select_as_group_payer"),
                              disabled:
                                payerIdentifier.isGroupPayer === true
                                  ? true
                                  : false,
                              onClick: () =>
                                handleMenuButton("select as group payer", item),
                            },
                            {
                              text: t("player_details"),
                              onClick: () =>
                                handleMenuButton("player details", item),
                            },
                          ]}
                          isMenuOpen={item.showMenu}
                          toggleMenu={() => {
                            toggleMenu("subscription", index);
                            setActionItemId(item.id);
                          }}
                        />
                      </div>
                    ) : (
                      <div className="details-settings">
                        <DotsMenu
                          menuItems={[
                            {
                              text: t("cancel_subscription"),
                              onClick: () =>
                                handleMenuButton("cancel subscription"),
                            },
                            {
                              text: t("player_details"),
                              onClick: () =>
                                handleMenuButton("player details", item),
                            },
                          ]}
                          isMenuOpen={item.showMenu}
                          toggleMenu={() => {
                            toggleMenu("subscription", index);
                            setActionItemId(item.id);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        <div className={`details-inputs-container ${invitationInputToggle}`}>
          <Input
            type="number"
            label={t("price")}
            name="price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
          />

          <div className="invite-cutomers-wrapper">
            <InviteCustomers
              setInviteInputValue={setInviteInputValue}
              inviteCutomer={handleInviteCutomer}
              deleteCustomer={handleDeleteCustomer}
              invitedCustomers={invitedCustomers}
              maxNumber={scheduleDetail.availableSeats}
              boldTitle={false}
              errorfromparent={inviteHasError}
            />
          </div>
        </div>

        <ModalConfirmation
          modalText={modalText}
          modalSubText={modalSubText}
          changeModal={setConfirmModalToggle}
          modalValue={confirmModalToggle}
          confirm={modalValue === 4 ? handleGroupPayer : postInvitation}
        />

        <ModalWarning
          warningText={warningModalText}
          buttonText={warningButtonText}
          changeModal={setWarningModalToggle}
          modalValue={warningModalToggle}
          Image={Tick}
          handleButton={warningButton}
        />

        {playerDetailsToggle && (
          <PlayerDetails
            playerName={playerDetails?.businessAccountName || ""}
            number={playerDetails?.phoneNumber || ""}
            email={playerDetails?.email || ""}
            changeModal={setPlayerDetailsToggle}
          />
        )}
      </div>

      <GlobalActionButtons
        negativeAction={() => {
          handleCancel(invitationInputToggle === "invite" ? "cancel" : "back");
        }}
        positiveAction={() => {
          handleValidations();
        }}
        negativButtonText={t(
          invitationInputToggle === "invite" ? "cancel" : "back",
        )}
        positiveButtonText={t("invite_customer")}
        negativButtonBgColor={
          invitationInputToggle === "invite"
            ? "var(--negative_bg_color)"
            : "var(--negative_bg_light_color)"
        }
        positiveButtonBgColor={"var(--positive_bg_color)"}
        negativButtonTextColor={
          invitationInputToggle === "invite" ? "#fff" : "black"
        }
        positiveButtonTextColor={"#fff"}
        disability={
          scheduleDetail.availableSeats === 0 ||
          (invitedCustomers.length === 0 && invitationInputToggle === "invite")
            ? true
            : false
        }
      />

      {loading && <Loader />}
    </div>
  );
};

export default ScheduleDetails;
